<template>
  <div class="flex flex-col items-center gap-8">
    <div class="flex items-center gap-3">
      <PaginationArrow
        :allow="normalizedCurrentPage > 1"
        @click="changePage(normalizedCurrentPage - 1)"
      >
        &lt;
      </PaginationArrow>
      <template v-for="page in pages" :key="page.key">
        <PaginationButton
          v-if="page.type === 'page'"
          :selected="page.num === normalizedCurrentPage"
          @click="changePage(page.num!)"
        >
          {{ page.num }}
        </PaginationButton>
        <span v-else class="px-1 font-helvetica-400 text-[14px] text-gray-dark"
          >...</span
        >
      </template>
      <PaginationArrow
        :allow="normalizedCurrentPage < normalizedTotalPages"
        @click="changePage(normalizedCurrentPage + 1)"
      >
        &gt;
      </PaginationArrow>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import PaginationButton from "~/components/goods/PaginationButton.vue";
import PaginationArrow from "~/components/goods/PaginationArrow.vue";

const props = defineProps<{
  currentPage: number;
  totalPages: number;
}>();

const emit = defineEmits<{
  (e: "change-page", page: number): void;
}>();

const normalizedTotalPages = computed(() => {
  const total = Math.floor(props.totalPages);
  return Number.isFinite(total) && total > 0 ? total : 1;
});
const normalizedCurrentPage = computed(() => {
  const current = Math.floor(props.currentPage);
  const safeCurrent = Number.isFinite(current) ? current : 1;
  return Math.min(Math.max(safeCurrent, 1), normalizedTotalPages.value);
});

const changePage = (page: number) => {
  const nextPage = Math.min(Math.max(page, 1), normalizedTotalPages.value);
  if (nextPage === normalizedCurrentPage.value) return;
  emit("change-page", nextPage);
};

type PageItem = {
  key: string | number;
  type: "page" | "ellipsis";
  num?: number;
};

const pages = computed<PageItem[]>(() => {
  const total = normalizedTotalPages.value;
  const current = normalizedCurrentPage.value;

  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => ({
      key: i + 1,
      type: "page",
      num: i + 1,
    }));
  }

  const items: PageItem[] = [];

  // начало списка
  if (current <= 4) {
    for (let page = 1; page <= 5; page += 1) {
      items.push({ key: page, type: "page", num: page });
    }
    items.push({ key: "end-ellipsis", type: "ellipsis" });
    items.push({ key: total, type: "page", num: total });
    return items;
  }

  // конец списка
  if (current >= total - 3) {
    items.push({ key: 1, type: "page", num: 1 });
    items.push({ key: "start-ellipsis", type: "ellipsis" });
    for (let page = total - 4; page <= total; page += 1) {
      items.push({ key: page, type: "page", num: page });
    }
    return items;
  }

  // середина
  items.push({ key: 1, type: "page", num: 1 });
  items.push({ key: "start-ellipsis", type: "ellipsis" });
  items.push(
    { key: current - 1, type: "page", num: current - 1 },
    { key: current, type: "page", num: current },
    { key: current + 1, type: "page", num: current + 1 },
  );
  items.push({ key: "end-ellipsis", type: "ellipsis" });
  items.push({ key: total, type: "page", num: total });

  return items;
});
</script>
